import React from 'react';
import {
  WxbButton,
  WxbCheckbox,
  WxbInput,
  WxbInputNumber,
  WxbSelect,
} from '../wxb-ui';
import { CYCLE_POLICY_TYPE_LABELS } from './equipmentStateCatalogModel';
import { formatAttributeOptionLabel } from './attributeCodeGeneration';
import type {
  EquipmentCalendarPattern,
  EquipmentCycleBaselineCapture,
  EquipmentCycleElapsedAmount,
  EquipmentCycleEventReference,
  EquipmentCycleExpiryRule,
  EquipmentMonthEndPolicy,
  EquipmentOrdinal,
  EquipmentStateAction,
  EquipmentStateAttribute,
  EquipmentWeekday,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  rule: EquipmentCycleExpiryRule;
  onChange: (rule: EquipmentCycleExpiryRule) => void;
  disabled?: boolean;
  depth?: number;
  baselineCaptures?: EquipmentCycleBaselineCapture[];
  attributes?: EquipmentStateAttribute[];
  actions?: EquipmentStateAction[];
}

interface EventEditorProps {
  value: EquipmentCycleEventReference;
  disabled: boolean;
  attributes: EquipmentStateAttribute[];
  actions: EquipmentStateAction[];
  onChange: (value: EquipmentCycleEventReference) => void;
}

const WEEKDAY_OPTIONS: Array<{ value: EquipmentWeekday; label: string }> = [
  { value: 'MONDAY', label: '周一' },
  { value: 'TUESDAY', label: '周二' },
  { value: 'WEDNESDAY', label: '周三' },
  { value: 'THURSDAY', label: '周四' },
  { value: 'FRIDAY', label: '周五' },
  { value: 'SATURDAY', label: '周六' },
  { value: 'SUNDAY', label: '周日' },
];

const MONTH_END_OPTIONS = [
  { value: 'CLAMP', label: '截到目标月最后一天' },
  { value: 'PRESERVE_END_OF_MONTH', label: '起点为月末时保持月末' },
  { value: 'ROLL_FORWARD', label: '顺延超出天数' },
  { value: 'SKIP', label: '日期不存在时跳过' },
];

const COMPARISON_OPTIONS = [
  { value: 'GTE', label: '大于等于' },
  { value: 'GT', label: '大于' },
  { value: 'LTE', label: '小于等于' },
  { value: 'LT', label: '小于' },
  { value: 'EQ', label: '等于' },
  { value: 'NE', label: '不等于' },
];

export const createElapsedCycleAmount = (
  value = 90,
  unit: EquipmentCycleElapsedAmount['unit'] = 'DAY',
): EquipmentCycleElapsedAmount => ({
  basis: 'ELAPSED_DURATION', value, unit, monthEndPolicy: null,
});

export const createDefaultCycleEvent = (
  kind: EquipmentCycleEventReference['kind'] = 'CLASS_ACTIVATION',
): EquipmentCycleEventReference => {
  if (kind === 'STATE_ENTRY') return { kind, attributeCode: '', valueCode: '' };
  if (kind === 'ACTION_STATUS') return { kind, actionCode: '', status: 'COMPLETED' };
  if (kind === 'METER_RESET') return { kind, meterCode: '' };
  if (kind === 'CUSTOM_EVENT') return { kind, eventCode: '' };
  return { kind };
};

export const createDefaultCycleRule = (
  kind: EquipmentCycleExpiryRule['kind'],
): EquipmentCycleExpiryRule => {
  if (kind === 'CALENDAR') {
    return {
      kind,
      period: { basis: 'CALENDAR_PERIOD', value: 3, unit: 'MONTH', monthEndPolicy: 'CLAMP' },
    };
  }
  if (kind === 'ABSOLUTE_DATE_TIME') return { kind, fixedDateTime: '' };
  if (kind === 'SCHEDULE') {
    return {
      kind,
      schedule: {
        kind: 'CALENDAR_PATTERN',
        pattern: { kind: 'WEEKLY', intervalWeeks: 1, weekdays: ['MONDAY'] },
        localTime: '09:00:00',
      },
    };
  }
  if (kind === 'ACCUMULATED') {
    return { kind, source: { kind: 'METER', code: '' }, baselineKey: '', delta: 1, unit: 'hour' };
  }
  if (kind === 'THRESHOLD') {
    return { kind, source: { kind: 'METER', code: '' }, operator: 'GTE', threshold: 1, unit: 'cycle' };
  }
  if (kind === 'CONDITION') {
    return { kind, signalCode: '', operator: 'GTE', threshold: 1, unit: null, sustainFor: null };
  }
  if (kind === 'EVENT') return { kind, event: createDefaultCycleEvent('CUSTOM_EVENT') };
  if (kind === 'COMPOSITE') {
    return {
      kind,
      operator: 'ANY_OF',
      satisfactionMode: 'LIVE',
      rules: [createDefaultCycleRule('ELAPSED'), createDefaultCycleRule('THRESHOLD')],
    };
  }
  return { kind: 'ELAPSED', duration: createElapsedCycleAmount() };
};

export const CycleEventReferenceEditor: React.FC<EventEditorProps> = ({
  value,
  disabled,
  attributes,
  actions,
  onChange,
}) => {
  const selectedAttribute = value.kind === 'STATE_ENTRY'
    ? attributes.find((attribute) => attribute.code === value.attributeCode)
    : null;
  return (
    <div className="esc-form-grid">
      <WxbSelect
        label="业务事件"
        value={value.kind}
        disabled={disabled}
        options={[
          { value: 'CLASS_ACTIVATION', label: '设备类别启用' },
          { value: 'RESOURCE_COMMISSIONED_AT', label: '设备投运' },
          { value: 'CYCLE_SATISFIED', label: '确认本周期已满足（审计事件）' },
          { value: 'STATE_ENTRY', label: '进入指定状态' },
          { value: 'ACTION_STATUS', label: '指定动作达到状态' },
          { value: 'METER_RESET', label: '仪表复位' },
          { value: 'CUSTOM_EVENT', label: '自定义事件' },
        ]}
        onChange={(kind) => onChange(createDefaultCycleEvent(kind as EquipmentCycleEventReference['kind']))}
      />
      {value.kind === 'STATE_ENTRY' && (
        <>
          <WxbSelect
            label="状态属性"
            value={value.attributeCode || undefined}
            disabled={disabled}
            options={attributes.filter((attribute) => attribute.kind === 'DISCRETE').map((attribute) => ({
              value: attribute.code,
              label: formatAttributeOptionLabel(attribute.name, attribute.code),
            }))}
            onChange={(attributeCode) => onChange({
              kind: 'STATE_ENTRY', attributeCode: String(attributeCode), valueCode: '',
            })}
          />
          <WxbSelect
            label="状态值"
            value={value.valueCode || undefined}
            disabled={disabled}
            options={(selectedAttribute?.values ?? []).map((stateValue) => ({
              value: stateValue.code,
              label: `${stateValue.name} (${stateValue.code})`,
            }))}
            onChange={(valueCode) => onChange({ ...value, valueCode: String(valueCode) })}
          />
        </>
      )}
      {value.kind === 'ACTION_STATUS' && (
        <>
          <WxbSelect
            label="动作"
            value={value.actionCode || undefined}
            disabled={disabled}
            options={actions.map((action) => ({
              value: action.actionCode,
              label: `${action.name} (${action.actionCode})`,
            }))}
            onChange={(actionCode) => onChange({ ...value, actionCode: String(actionCode) })}
          />
          <WxbSelect
            label="动作状态"
            value={value.status}
            disabled={disabled}
            options={[
              { value: 'COMPLETED', label: '已完成' },
              { value: 'RELEASED', label: '已放行' },
            ]}
            onChange={(status) => onChange({ ...value, status: status as typeof value.status })}
          />
        </>
      )}
      {value.kind === 'METER_RESET' && (
        <WxbInput
          label="仪表编码"
          value={value.meterCode}
          disabled={disabled}
          onChange={(event) => onChange({ ...value, meterCode: event.target.value })}
        />
      )}
      {value.kind === 'CUSTOM_EVENT' && (
        <WxbInput
          label="事件编码"
          value={value.eventCode}
          disabled={disabled}
          onChange={(event) => onChange({ ...value, eventCode: event.target.value })}
        />
      )}
    </div>
  );
};

const CycleRuleEditor: React.FC<Props> = ({
  rule,
  onChange,
  disabled = false,
  depth = 1,
  baselineCaptures = [],
  attributes = [],
  actions = [],
}) => {
  const availableKinds = (Object.keys(CYCLE_POLICY_TYPE_LABELS) as EquipmentCycleExpiryRule['kind'][])
    .filter((kind) => depth < 3 || kind !== 'COMPOSITE');

  const updateCalendarPattern = (patch: Record<string, unknown>) => {
    if (rule.kind !== 'SCHEDULE' || rule.schedule.kind !== 'CALENDAR_PATTERN') return;
    onChange({
      ...rule,
      schedule: {
        ...rule.schedule,
        pattern: { ...rule.schedule.pattern, ...patch } as EquipmentCalendarPattern,
      },
    });
  };

  return (
    <div className="esc-cycle-rule-editor">
      <WxbSelect
        label={depth === 1 ? '到期判定方式' : `条件 ${depth} 类型`}
        value={rule.kind}
        disabled={disabled}
        options={availableKinds.map((kind) => ({ value: kind, label: CYCLE_POLICY_TYPE_LABELS[kind] }))}
        onChange={(kind) => onChange(createDefaultCycleRule(kind as EquipmentCycleExpiryRule['kind']))}
      />

      {rule.kind === 'ELAPSED' && (
        <div className="esc-form-grid">
          <WxbInputNumber
            label="连续时长"
            min={0.000001}
            value={rule.duration.value}
            disabled={disabled}
            onChange={(value) => onChange({
              ...rule,
              duration: { ...rule.duration, value: typeof value === 'number' ? value : 1 },
            })}
          />
          <WxbSelect
            label="单位"
            value={rule.duration.unit}
            disabled={disabled}
            options={[
              { value: 'SECOND', label: '秒' },
              { value: 'MINUTE', label: '分钟' },
              { value: 'HOUR', label: '小时' },
              { value: 'DAY', label: '连续天（24 小时）' },
            ]}
            onChange={(unit) => onChange({ ...rule, duration: { ...rule.duration, unit: unit as typeof rule.duration.unit } })}
          />
        </div>
      )}

      {rule.kind === 'CALENDAR' && (
        <div className="esc-form-grid esc-form-grid-three">
          <WxbInputNumber
            label="日历周期"
            min={1}
            precision={0}
            value={rule.period.value}
            disabled={disabled}
            onChange={(value) => onChange({
              ...rule,
              period: { ...rule.period, value: typeof value === 'number' ? value : 1 },
            })}
          />
          <WxbSelect
            label="单位"
            value={rule.period.unit}
            disabled={disabled}
            options={[
              { value: 'DAY', label: '自然日' },
              { value: 'WEEK', label: '自然周' },
              { value: 'MONTH', label: '自然月' },
              { value: 'QUARTER', label: '自然季度' },
              { value: 'YEAR', label: '自然年' },
            ]}
            onChange={(unit) => {
              const typedUnit = unit as typeof rule.period.unit;
              onChange({
                ...rule,
                period: {
                  ...rule.period,
                  unit: typedUnit,
                  monthEndPolicy: ['MONTH', 'QUARTER', 'YEAR'].includes(typedUnit)
                    ? rule.period.monthEndPolicy ?? 'CLAMP'
                    : null,
                },
              });
            }}
          />
          {['MONTH', 'QUARTER', 'YEAR'].includes(rule.period.unit) && (
            <WxbSelect
              label="月末处理"
              value={rule.period.monthEndPolicy ?? 'CLAMP'}
              disabled={disabled}
              options={MONTH_END_OPTIONS}
              onChange={(monthEndPolicy) => onChange({
                ...rule,
                period: { ...rule.period, monthEndPolicy: monthEndPolicy as EquipmentMonthEndPolicy },
              })}
            />
          )}
        </div>
      )}

      {rule.kind === 'ABSOLUTE_DATE_TIME' && (
        <WxbInput
          label="固定截止时刻"
          value={rule.fixedDateTime}
          disabled={disabled}
          placeholder="2026-10-31T17:00:00+08:00"
          helpText="请填写带时区偏移的完整时间。"
          onChange={(event) => onChange({ ...rule, fixedDateTime: event.target.value })}
        />
      )}

      {rule.kind === 'SCHEDULE' && (
        <div className="esc-modal-stack">
          <WxbSelect
            label="排期方式"
            value={rule.schedule.kind}
            disabled={disabled}
            options={[
              { value: 'CALENDAR_PATTERN', label: '按日历模式' },
              { value: 'BUSINESS_INTERVAL', label: '按业务日历间隔' },
            ]}
            onChange={(kind) => onChange({
              kind: 'SCHEDULE',
              schedule: kind === 'BUSINESS_INTERVAL'
                ? { kind, value: 1, unit: 'BUSINESS_DAY', businessCalendarCode: '' }
                : {
                  kind: 'CALENDAR_PATTERN',
                  pattern: { kind: 'WEEKLY', intervalWeeks: 1, weekdays: ['MONDAY'] },
                  localTime: '09:00:00',
                },
            })}
          />
          {rule.schedule.kind === 'BUSINESS_INTERVAL' ? (
            <div className="esc-form-grid esc-form-grid-three">
              <WxbInputNumber
                label="间隔"
                min={1}
                precision={0}
                value={rule.schedule.value}
                disabled={disabled}
                onChange={(value) => {
                  if (rule.kind !== 'SCHEDULE' || rule.schedule.kind !== 'BUSINESS_INTERVAL') return;
                  onChange({ ...rule, schedule: { ...rule.schedule, value: typeof value === 'number' ? value : 1 } });
                }}
              />
              <WxbSelect
                label="单位"
                value={rule.schedule.unit}
                disabled={disabled}
                options={[
                  { value: 'BUSINESS_DAY', label: '工作日' },
                  { value: 'BUSINESS_WEEK', label: '工作周' },
                ]}
                onChange={(unit) => {
                  if (rule.kind !== 'SCHEDULE' || rule.schedule.kind !== 'BUSINESS_INTERVAL') return;
                  onChange({ ...rule, schedule: { ...rule.schedule, unit: unit as typeof rule.schedule.unit } });
                }}
              />
              <WxbInput
                label="业务日历编码"
                value={rule.schedule.businessCalendarCode}
                disabled={disabled}
                placeholder="例如 CN-SH-WORKDAY"
                onChange={(event) => {
                  if (rule.kind !== 'SCHEDULE' || rule.schedule.kind !== 'BUSINESS_INTERVAL') return;
                  onChange({ ...rule, schedule: { ...rule.schedule, businessCalendarCode: event.target.value } });
                }}
              />
            </div>
          ) : (
            <div className="esc-form-grid esc-form-grid-three">
              <WxbSelect
                label="日历模式"
                value={rule.schedule.pattern.kind}
                disabled={disabled}
                options={[
                  { value: 'WEEKLY', label: '每 N 周指定星期' },
                  { value: 'MONTHLY_DAY', label: '每 N 月指定日期' },
                  { value: 'MONTHLY_NTH_WEEKDAY', label: '每 N 月第几个星期几' },
                  { value: 'YEARLY_DATE', label: '每 N 年指定月日' },
                ]}
                onChange={(kind) => {
                  const pattern: EquipmentCalendarPattern = kind === 'MONTHLY_DAY'
                    ? { kind, intervalMonths: 1, dayOfMonth: 1, monthEndPolicy: 'CLAMP' }
                    : kind === 'MONTHLY_NTH_WEEKDAY'
                      ? { kind, intervalMonths: 1, ordinal: 'FIRST', weekday: 'MONDAY' }
                      : kind === 'YEARLY_DATE'
                        ? { kind, intervalYears: 1, month: 1, day: 1, monthEndPolicy: 'CLAMP' }
                        : { kind: 'WEEKLY', intervalWeeks: 1, weekdays: ['MONDAY'] };
                  if (rule.kind !== 'SCHEDULE' || rule.schedule.kind !== 'CALENDAR_PATTERN') return;
                  onChange({ ...rule, schedule: { ...rule.schedule, pattern } });
                }}
              />
              <WxbInput
                label="本地到期时间"
                value={rule.schedule.localTime}
                disabled={disabled}
                placeholder="HH:mm:ss"
                onChange={(event) => {
                  if (rule.kind !== 'SCHEDULE' || rule.schedule.kind !== 'CALENDAR_PATTERN') return;
                  onChange({ ...rule, schedule: { ...rule.schedule, localTime: event.target.value } });
                }}
              />
              {rule.schedule.pattern.kind === 'WEEKLY' && (
                <>
                  <WxbInputNumber
                    label="间隔周数"
                    min={1}
                    precision={0}
                    value={rule.schedule.pattern.intervalWeeks}
                    disabled={disabled}
                    onChange={(value) => updateCalendarPattern({ intervalWeeks: typeof value === 'number' ? value : 1 })}
                  />
                  <div className="esc-form-span">
                    <WxbSelect
                      label="星期"
                      mode="multiple"
                      value={rule.schedule.pattern.weekdays}
                      disabled={disabled}
                      options={WEEKDAY_OPTIONS}
                      onChange={(weekdays) => updateCalendarPattern({ weekdays: weekdays as EquipmentWeekday[] })}
                    />
                  </div>
                </>
              )}
              {rule.schedule.pattern.kind === 'MONTHLY_DAY' && (
                <>
                  <WxbInputNumber label="间隔月数" min={1} precision={0} value={rule.schedule.pattern.intervalMonths} disabled={disabled} onChange={(value) => updateCalendarPattern({ intervalMonths: typeof value === 'number' ? value : 1 })} />
                  <WxbInputNumber label="每月第几日" min={1} max={31} precision={0} value={rule.schedule.pattern.dayOfMonth} disabled={disabled} onChange={(value) => updateCalendarPattern({ dayOfMonth: typeof value === 'number' ? value : 1 })} />
                  <WxbSelect label="月末处理" value={rule.schedule.pattern.monthEndPolicy} disabled={disabled} options={MONTH_END_OPTIONS} onChange={(value) => updateCalendarPattern({ monthEndPolicy: value as EquipmentMonthEndPolicy })} />
                </>
              )}
              {rule.schedule.pattern.kind === 'MONTHLY_NTH_WEEKDAY' && (
                <>
                  <WxbInputNumber label="间隔月数" min={1} precision={0} value={rule.schedule.pattern.intervalMonths} disabled={disabled} onChange={(value) => updateCalendarPattern({ intervalMonths: typeof value === 'number' ? value : 1 })} />
                  <WxbSelect label="序数" value={rule.schedule.pattern.ordinal} disabled={disabled} options={[
                    { value: 'FIRST', label: '第一个' }, { value: 'SECOND', label: '第二个' },
                    { value: 'THIRD', label: '第三个' }, { value: 'FOURTH', label: '第四个' },
                    { value: 'LAST', label: '最后一个' },
                  ]} onChange={(ordinal) => updateCalendarPattern({ ordinal: ordinal as EquipmentOrdinal })} />
                  <WxbSelect label="星期" value={rule.schedule.pattern.weekday} disabled={disabled} options={WEEKDAY_OPTIONS} onChange={(weekday) => updateCalendarPattern({ weekday: weekday as EquipmentWeekday })} />
                </>
              )}
              {rule.schedule.pattern.kind === 'YEARLY_DATE' && (
                <>
                  <WxbInputNumber label="间隔年数" min={1} precision={0} value={rule.schedule.pattern.intervalYears} disabled={disabled} onChange={(value) => updateCalendarPattern({ intervalYears: typeof value === 'number' ? value : 1 })} />
                  <WxbInputNumber label="月份" min={1} max={12} precision={0} value={rule.schedule.pattern.month} disabled={disabled} onChange={(value) => updateCalendarPattern({ month: typeof value === 'number' ? value : 1 })} />
                  <WxbInputNumber label="日期" min={1} max={31} precision={0} value={rule.schedule.pattern.day} disabled={disabled} onChange={(value) => updateCalendarPattern({ day: typeof value === 'number' ? value : 1 })} />
                  <WxbSelect label="月末处理" value={rule.schedule.pattern.monthEndPolicy} disabled={disabled} options={MONTH_END_OPTIONS} onChange={(value) => updateCalendarPattern({ monthEndPolicy: value as EquipmentMonthEndPolicy })} />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {rule.kind === 'ACCUMULATED' && (
        <div className="esc-form-grid esc-form-grid-four">
          <WxbSelect
            label="累计数据来源（仪表 / 信号）"
            value={rule.source.kind}
            disabled={disabled}
            options={[{ value: 'METER', label: '仪表' }, { value: 'SIGNAL', label: '信号' }]}
            onChange={(kind) => onChange({ ...rule, source: { ...rule.source, kind: kind as typeof rule.source.kind } })}
          />
          <WxbInput label="来源编码" value={rule.source.code} disabled={disabled} onChange={(event) => onChange({ ...rule, source: { ...rule.source, code: event.target.value } })} />
          <WxbSelect
            label="启动时基线"
            value={rule.baselineKey || undefined}
            disabled={disabled}
            options={baselineCaptures.map((capture) => ({ value: capture.key, label: capture.key }))}
            onChange={(baselineKey) => onChange({ ...rule, baselineKey: String(baselineKey) })}
          />
          <WxbInputNumber label="累计增量" min={0.000001} value={rule.delta} disabled={disabled} onChange={(value) => onChange({ ...rule, delta: typeof value === 'number' ? value : 1 })} />
          <WxbInput label="单位" value={rule.unit} disabled={disabled} placeholder="例如 hour、cycle" onChange={(event) => onChange({ ...rule, unit: event.target.value })} />
        </div>
      )}

      {rule.kind === 'THRESHOLD' && (
        <div className="esc-form-grid esc-form-grid-four">
          <WxbSelect label="测量来源" value={rule.source.kind} disabled={disabled} options={[{ value: 'METER', label: '仪表' }, { value: 'SIGNAL', label: '信号' }]} onChange={(kind) => onChange({ ...rule, source: { ...rule.source, kind: kind as typeof rule.source.kind } })} />
          <WxbInput label="来源编码" value={rule.source.code} disabled={disabled} onChange={(event) => onChange({ ...rule, source: { ...rule.source, code: event.target.value } })} />
          <WxbSelect label="比较方式" value={rule.operator} disabled={disabled} options={COMPARISON_OPTIONS} onChange={(operator) => onChange({ ...rule, operator: operator as typeof rule.operator })} />
          <WxbInputNumber label="判定值" value={rule.threshold} disabled={disabled} onChange={(value) => onChange({ ...rule, threshold: typeof value === 'number' ? value : 0 })} />
          <WxbInput label="单位" value={rule.unit} disabled={disabled} onChange={(event) => onChange({ ...rule, unit: event.target.value })} />
        </div>
      )}

      {rule.kind === 'CONDITION' && (
        <div className="esc-form-grid esc-form-grid-four">
          <WxbInput label="信号编码" value={rule.signalCode} disabled={disabled} onChange={(event) => onChange({ ...rule, signalCode: event.target.value })} />
          <WxbSelect label="比较方式" value={rule.operator} disabled={disabled} options={COMPARISON_OPTIONS} onChange={(operator) => onChange({ ...rule, operator: operator as typeof rule.operator })} />
          <WxbInput
            label="判定值"
            value={String(rule.threshold)}
            disabled={disabled}
            onChange={(event) => {
              const raw = event.target.value;
              const threshold = raw === 'true' ? true : raw === 'false' ? false
                : raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : raw;
              onChange({ ...rule, threshold });
            }}
          />
          <WxbInput label="单位（可选）" value={rule.unit ?? ''} disabled={disabled} onChange={(event) => onChange({ ...rule, unit: event.target.value || null })} />
          <div className="esc-form-span esc-optional-rule">
            <WxbCheckbox checked={Boolean(rule.sustainFor)} disabled={disabled} onChange={(checked) => onChange({ ...rule, sustainFor: checked ? createElapsedCycleAmount(1, 'HOUR') : null })}>
              条件需要持续一段时间
            </WxbCheckbox>
            {rule.sustainFor && (
              <div className="esc-form-grid">
                <WxbInputNumber label="持续时长" min={0.000001} value={rule.sustainFor.value} disabled={disabled} onChange={(value) => onChange({ ...rule, sustainFor: { ...rule.sustainFor!, value: typeof value === 'number' ? value : 1 } })} />
                <WxbSelect label="单位" value={rule.sustainFor.unit} disabled={disabled} options={[
                  { value: 'SECOND', label: '秒' }, { value: 'MINUTE', label: '分钟' },
                  { value: 'HOUR', label: '小时' }, { value: 'DAY', label: '连续天' },
                ]} onChange={(unit) => onChange({ ...rule, sustainFor: { ...rule.sustainFor!, unit: unit as EquipmentCycleElapsedAmount['unit'] } })} />
              </div>
            )}
          </div>
        </div>
      )}

      {rule.kind === 'EVENT' && (
        <CycleEventReferenceEditor value={rule.event} disabled={disabled} attributes={attributes} actions={actions} onChange={(event) => onChange({ ...rule, event })} />
      )}

      {rule.kind === 'COMPOSITE' && (
        <div className="esc-composite-editor">
          <WxbSelect
            label="组合逻辑"
            value={rule.operator}
            disabled={disabled}
            options={[
              { value: 'ANY_OF', label: '任一条件满足即到期' },
              { value: 'ALL_OF', label: '全部条件满足才到期' },
            ]}
            onChange={(operator) => onChange({ ...rule, operator: operator as typeof rule.operator })}
          />
          <WxbSelect
            label="条件满足方式"
            value={rule.satisfactionMode}
            disabled={disabled}
            options={[
              { value: 'LIVE', label: '同时满足（实时）' },
              { value: 'LATCH_UNTIL_REFRESH', label: '分别满足后保持到周期刷新' },
            ]}
            onChange={(satisfactionMode) => onChange({
              ...rule,
              satisfactionMode: satisfactionMode as typeof rule.satisfactionMode,
            })}
          />
          {rule.rules.map((child, index) => (
            <div className="esc-composite-child" key={`${child.kind}-${index}`}>
              <div className="esc-subsection-header">
                <strong>条件 {index + 1}</strong>
                {!disabled && rule.rules.length > 2 && (
                  <WxbButton type="button" size="sm" variant="ghost" onClick={() => onChange({ ...rule, rules: rule.rules.filter((_, childIndex) => childIndex !== index) })}>
                    删除条件
                  </WxbButton>
                )}
              </div>
              <CycleRuleEditor
                rule={child}
                disabled={disabled}
                depth={depth + 1}
                baselineCaptures={baselineCaptures}
                attributes={attributes}
                actions={actions}
                onChange={(nextChild) => onChange({
                  ...rule,
                  rules: rule.rules.map((item, childIndex) => childIndex === index ? nextChild : item),
                })}
              />
            </div>
          ))}
          {!disabled && rule.rules.length < 20 && (
            <WxbButton type="button" size="sm" variant="secondary" onClick={() => onChange({ ...rule, rules: [...rule.rules, createDefaultCycleRule('ELAPSED')] })}>
              添加条件
            </WxbButton>
          )}
        </div>
      )}
    </div>
  );
};

export default CycleRuleEditor;
